import { moneyFormat } from "@redotech/money/util";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import {
  CheckoutOptimizationWidgetType,
  checkoutOptimizationWidgetTypeToLabel,
} from "@redotech/redo-conversion-core-common/checkouts/widget";
import { canUsePreviewMode } from "@redotech/redo-conversion-core-common/conversion/features";
import {
  CheckoutExperience,
  PREVIEW_CHECKOUT_OUTBOUND_RATES_NAME,
} from "@redotech/redo-conversion-core-common/redo-in-shipping/ris";
import { ShippingRateTable } from "@redotech/redo-conversion-core-common/redo-in-shipping/shipping-rate-table";
import { useTranslation } from "@redotech/redo-i18n";
import AlarmClockIcon from "@redotech/redo-icons/alarm-clock.svg";
import CopyIcon from "@redotech/redo-icons/copy-03.svg";
import ImageIcon from "@redotech/redo-icons/image-01.svg";
import LayoutGridIcon from "@redotech/redo-icons/layout-grid-01.svg";
import LinkExternalIcon from "@redotech/redo-icons/link-external-02.svg";
import StarIcon from "@redotech/redo-icons/star-01.svg";
import TagIcon from "@redotech/redo-icons/tag-01.svg";
import ShippingTruckIcon from "@redotech/redo-icons/truck-01.svg";
import { TeamContext } from "@redotech/redo-merchant-app-common/team";
import {
  InspectorShippingMethodItem,
  ShippingMethodItemDisplayTableRows,
} from "@redotech/redo-web-conversion/checkouts/shipping/shipping-method-item";
import { toast } from "@redotech/redo-web/alert";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { TextRedoListItem } from "@redotech/redo-web/arbiter-components/list/standard-redo-list-items";
import { Tooltip } from "@redotech/redo-web/arbiter-components/tooltip/tooltip";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { isDef } from "@redotech/util/checks";
import { JSXElementConstructor, memo, useContext, useMemo } from "react";
import { useParams } from "react-router-dom";
import { CheckoutTreeAnalyticsContext } from "../../../../../conversion/context/checkout-tree-analytics-provider";

export const CheckoutExperienceContent = memo(
  function CheckoutExperienceContent({
    nodeId,
    checkoutExperience,
    widgets,
    showName,
    readonly,
  }: {
    nodeId: string;
    checkoutExperience: CheckoutExperience;
    widgets: { id: string; type: CheckoutOptimizationWidgetType }[] | undefined;
    showName: boolean;
    readonly: boolean;
  }) {
    const { t } = useTranslation();
    const teamId = useParams().teamId;
    const team = useRequiredContext(TeamContext);

    const showPreviewButton = useMemo(
      () =>
        canUsePreviewMode({
          checkoutOptimizationSettings: team.settings?.checkoutOptimization,
        }),
      [team.settings?.checkoutOptimization],
    );

    const handleNavigateToCheckoutExperiencePage = useHandler(() => {
      if (!teamId) {
        return;
      }

      const url = `/stores/${teamId}/conversion/checkouts/${checkoutExperience._id}`;
      window.open(url, "_blank");
    });

    const handleCopyPreviewCode = useHandler(async () => {
      const previewCode = `${PREVIEW_CHECKOUT_OUTBOUND_RATES_NAME}-${nodeId}`;
      await navigator.clipboard.writeText(previewCode);
      toast(
        t('Copied preview code "{{previewCode}}" to clipboard', {
          previewCode,
        }),
        { variant: "success" },
      );
    });

    return (
      <>
        {showName && (
          <Flex align="flex-start" dir="row" gap="md" justify="space-between">
            <Flex dir="column" gap="xs" justify="flex-start">
              <Text fontSize="xs" fontWeight="medium" textColor="secondary">
                {t("Checkout name")}
              </Text>
              <Text fontSize="xs" fontWeight="medium" textColor="primary">
                {checkoutExperience.name}
              </Text>
            </Flex>
            <RedoButton
              aria-label={t("Open checkout experience")}
              hierarchy="tertiary"
              IconLeading={LinkExternalIcon}
              onClick={handleNavigateToCheckoutExperiencePage}
              size="xs"
            />
          </Flex>
        )}
        {widgets && widgets.length > 0 ? (
          <Flex dir="column" gap="xl" justify="flex-start">
            <Text fontSize="md" fontWeight="semibold">
              {t("Widgets")}
            </Text>
            <Flex dir="column" gap="md" w="full">
              {widgets.map((widget) => (
                <WidgetListItem
                  key={widget.id}
                  teamId={teamId}
                  widgetId={widget.id}
                  widgetType={widget.type}
                />
              ))}
            </Flex>
          </Flex>
        ) : null}
        <Flex dir="column" gap="xl" justify="flex-start">
          <Flex align="center" dir="row" gap="md" justify="flex-start">
            <Text fontSize="md" fontWeight="semibold">
              {t("Shipping methods")}
            </Text>
            {showPreviewButton && (
              <Tooltip
                subtitle={t(
                  "Enter the copied code as the first name in the checkout to preview this experience.",
                )}
                title={t("Copy preview code")}
              >
                <RedoButton
                  aria-label={t("Copy preview code")}
                  hierarchy="tertiary"
                  IconLeading={CopyIcon}
                  onClick={handleCopyPreviewCode}
                  size="xs"
                />
              </Tooltip>
            )}
          </Flex>
          <Flex dir="column" gap="md">
            {checkoutExperience.rateTables.map((rateTable, index) => (
              <InspectorShippingMethodItemWrapper
                key={index}
                nodeId={nodeId}
                rateTable={rateTable}
                readonly={readonly}
                useConstraintsV2={!!checkoutExperience.useConstraintsV2}
              />
            ))}
          </Flex>
        </Flex>
      </>
    );
  },
);

const InspectorShippingMethodItemWrapper = memo(
  function InspectorShippingMethodItemWrapper({
    rateTable,
    nodeId,
    readonly,
    useConstraintsV2,
  }: {
    rateTable: ShippingRateTable;
    nodeId: string;
    readonly: boolean;
    useConstraintsV2: boolean;
  }) {
    const { t } = useTranslation();
    const { analyticsForCheckoutNodeByNodeId, checkoutTreeAnalyticsLoad } =
      useContext(CheckoutTreeAnalyticsContext);

    const displayTableRows: ShippingMethodItemDisplayTableRows = useMemo(() => {
      // if in edit mode, don't show metrics
      if (!readonly) {
        return { pending: false, value: [] };
      }

      if (
        checkoutTreeAnalyticsLoad.pending &&
        !analyticsForCheckoutNodeByNodeId[nodeId]
      ) {
        return { pending: true };
      } else if (!analyticsForCheckoutNodeByNodeId[nodeId]) {
        return { pending: false, value: [] };
      } else {
        const rateTableAnalytics =
          analyticsForCheckoutNodeByNodeId[nodeId]?.byRateTableId[
            rateTable._id
          ];
        if (!rateTableAnalytics) {
          return {
            pending: false,
            value: [
              { label: t("Orders"), value: "-" },
              { label: t("Revenue"), value: "-" },
              { label: t("Shipping revenue"), value: "-" },
            ],
          };
        }

        return {
          pending: false,
          value: [
            {
              label: t("Orders"),
              value: `${rateTableAnalytics.orders.count} (${rateTableAnalytics.orders.relativePercentage.toFixed(1)}%)`,
            },
            {
              label: t("Revenue"),
              value: moneyFormat(rateTableAnalytics.revenue),
            },
            {
              label: t("Shipping revenue"),
              value:
                rateTableAnalytics.orders.count > 0
                  ? moneyFormat(rateTableAnalytics.shippingRevenue)
                  : "-",
            },
          ].filter(isDef),
        };
      }
    }, [
      analyticsForCheckoutNodeByNodeId,
      checkoutTreeAnalyticsLoad,
      nodeId,
      rateTable,
      readonly,
      t,
    ]);

    return (
      <InspectorShippingMethodItem
        displayTableRows={displayTableRows}
        rateTable={{ ...rateTable, id: rateTable._id }}
        useConstraintsV2={useConstraintsV2}
      />
    );
  },
);

const widgetTypeToIcon: Record<
  CheckoutOptimizationWidgetType,
  JSXElementConstructor<any>
> = {
  [CheckoutOptimizationWidgetType.FULFILLMENT_CUTOFF]: AlarmClockIcon,
  [CheckoutOptimizationWidgetType.THRESHOLD_UPSELL]: TagIcon,
  [CheckoutOptimizationWidgetType.LOW_PROFILE_PRODUCT_UPSELL]: TagIcon,
  [CheckoutOptimizationWidgetType.CONTENT_BLOCK]: LayoutGridIcon,
  [CheckoutOptimizationWidgetType.IMAGE_BLOCK]: ImageIcon,
  [CheckoutOptimizationWidgetType.SHIPPING_OPTION_CONTENT]: ShippingTruckIcon,
  [CheckoutOptimizationWidgetType.SHIPPING_OPTION_IMAGE]: ImageIcon,
  [CheckoutOptimizationWidgetType.SHIPPING_OPTION_PRODUCT_UPSELL]: TagIcon,
  [CheckoutOptimizationWidgetType.FAILED_DISCOUNT_RESCUE]: TagIcon,
  [CheckoutOptimizationWidgetType.PRODUCT_REVIEWS]: StarIcon,
};

const WidgetListItem = memo(function WidgetListItem({
  widgetId,
  widgetType,
  teamId,
}: {
  widgetId: string;
  widgetType: CheckoutOptimizationWidgetType;
  teamId: string | undefined;
}) {
  const handleClick = useHandler(() => {
    if (!teamId) {
      return;
    }
    window.open(`/stores/${teamId}/conversion/widgets/${widgetId}`, "_blank");
  });

  return (
    <TextRedoListItem
      card
      leadingItem={{ type: "icon", Icon: widgetTypeToIcon[widgetType] }}
      onItemSelected={handleClick}
      size="sm"
      text={checkoutOptimizationWidgetTypeToLabel[widgetType]}
    />
  );
});
